import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { fmt } from '@components/ProductOffers/modals/ShoppingCartDrawer/utils/pricingSummary.utils';
import type { ChargeSectionProps } from '@components/ProductOffers/modals/ShoppingCartDrawer/PricingSummary/PricingSummary.types';
import {
  DiscountText,
  ItemDivider,
  PricingCard,
  PricingRow,
  TotalDivider,
} from '@components/ProductOffers/modals/ShoppingCartDrawer/ShoppingCartDrawer.styles';

const ChargeSection = ({ title, description, breakdown, showShipping }: ChargeSectionProps) => {
  const hasItems = breakdown.items.length > 0;

  return (
    <Stack spacing={1.25} useFlexGap>
      <Typography variant="price.total" className="ChargeSection-title">
        {title}
      </Typography>

      {description && <Typography variant="body.sm">{description}</Typography>}

      {showShipping && (
        <PricingRow>
          <Typography variant="body.emphasis">Installation Fee:</Typography>
          <Typography variant="label.field">{fmt(0)}</Typography>
        </PricingRow>
      )}

      {hasItems && (
        <PricingCard>
          {breakdown.items.map((item, i) => (
            <Box key={i}>
              {i > 0 && <ItemDivider sx={{ mb: '8px' }} />}
              <PricingRow>
                <Typography variant="body.emphasis">{item.name}</Typography>
                <Typography variant="label.field">{fmt(item.amount)}</Typography>
              </PricingRow>
              {item.children.map((child, j) => (
                <PricingRow key={j} sx={{ pl: '10px', mt: '4px' }}>
                  <Typography variant="body.sm">
                    &bull;&nbsp;{child.name}
                    {child.typeSuffix ? ` - ${child.typeSuffix}` : ''}
                    {(child.quantity ?? 1) > 1 ? ` x ${child.quantity}` : ''}
                  </Typography>
                  <Typography variant="label.field">{fmt(child.amount)}</Typography>
                </PricingRow>
              ))}
            </Box>
          ))}

          {breakdown.discounts.length > 0 && (
            <>
              <ItemDivider />
              <Box>
                <Typography variant="body.emphasis">Discounts</Typography>
                {breakdown.discounts.map((d, i) => (
                  <PricingRow key={i} sx={{ pl: '10px', mt: '4px' }}>
                    <Typography variant="body.sm">&bull;&nbsp;{d.name}</Typography>
                    <DiscountText>-{fmt(Math.abs(d.amount))}</DiscountText>
                  </PricingRow>
                ))}
              </Box>
              <ItemDivider />
            </>
          )}

          {hasItems && breakdown.taxLines.length > 0 && (
            <>
              <ItemDivider />
              <Box>
                <Typography variant="label.field">Estimated Taxes</Typography>
                {breakdown.taxLines.map((tax, i) => (
                  <PricingRow key={i} sx={{ pl: '10px', mt: '4px' }}>
                    <Typography variant="body.sm">&bull;&nbsp;{tax.name}</Typography>
                    <Typography variant="label.field">{fmt(tax.amount)}</Typography>
                  </PricingRow>
                ))}
              </Box>
            </>
          )}

          <TotalDivider />
          <PricingRow>
            <Typography variant="body.emphasis">Total</Typography>
            <Typography variant="label.button">{fmt(breakdown.total)}</Typography>
          </PricingRow>
          <Typography variant="body.sm" display="inline">
            All prices are in{' '}
            <Typography variant="discount.text" component="span" display="inline">
              Canadian Dollars (CAD)
            </Typography>
          </Typography>
        </PricingCard>
      )}
    </Stack>
  );
};

export default ChargeSection;
